import { Clock, EnvelopeOpen } from '@phosphor-icons/react'
import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { BusyLabel } from '@/components/busy-label'
import { authApi } from '../api'
import { AuthLayout } from '../components/auth-layout'
import { Notice, type NoticeTone } from '../components/notice'
import { friendlyErrorKey } from '@/lib/api/friendly-errors'
import {
  verificationWindowMinutes,
  type VerifyRegistrationRouteState,
} from '../verification'

/**
 * Post-registration instructions (UI_PARITY §3.4, D-049). The email address,
 * the delivery result and the link lifetime come from the registration
 * response; the account itself exists only after the emailed link is used, so
 * this screen never claims an account was created.
 */
export function VerifyRegistrationScreen() {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state ?? {}) as VerifyRegistrationRouteState
  const email = typeof state.email === 'string' ? state.email : ''
  const deliveryAvailable = state.emailDeliveryAvailable === true
  const minutes = verificationWindowMinutes(state.expiresInSeconds)

  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<{
    key: string
    tone: NoticeTone
  } | null>(null)

  async function resend() {
    if (!email) {
      setNotice({ key: 'auth.verifyRegistration.missingEmail', tone: 'error' })
      return
    }
    setBusy(true)
    setNotice(null)
    try {
      const response = await authApi.resendVerificationEmail(email)
      setNotice({
        key: response.emailDeliveryAvailable
          ? 'auth.verifyRegistration.resendSent'
          : 'auth.verifyRegistration.resendUnavailable',
        tone: response.emailDeliveryAvailable ? 'success' : 'info',
      })
    } catch (error) {
      setNotice({ key: friendlyErrorKey(error), tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthLayout
      title={t('screens.verifyRegistration.title')}
      maxWidth={520}
      backTo="/login"
      titleInChildren
    >
      <div className="flex flex-col items-center text-center">
        <EnvelopeOpen
          size={70}
          weight="bold"
          aria-hidden="true"
          className="text-teal"
        />
        <h1 className="mt-5 text-[25px] leading-none font-extrabold text-deep">
          {t('screens.verifyRegistration.title')}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">
          {!email
            ? t('auth.verifyRegistration.missingEmail')
            : deliveryAvailable
              ? t('auth.verifyRegistration.sentTo', { email })
              : t('auth.verifyRegistration.deliveryUnavailable')}
        </p>

        {email ? (
          <>
            <p className="mt-3 inline-flex items-center gap-2 rounded-md bg-paper px-3 py-2 text-sm font-semibold text-deep">
              <Clock
                size={18}
                weight="bold"
                aria-hidden="true"
                className="text-teal"
              />
              {t('auth.verifyRegistration.expiresIn', { count: minutes })}
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted">
              {t('auth.verifyRegistration.pendingAccount')}
            </p>
          </>
        ) : null}

        {notice ? (
          <Notice tone={notice.tone} className="mt-4 w-full text-left">
            {t(notice.key, { count: minutes })}
          </Notice>
        ) : null}

        {email ? (
          <button
            type="button"
            className="btn-secondary mt-6 w-full"
            onClick={() => void resend()}
            disabled={busy}
            aria-busy={busy}
          >
            <BusyLabel
              busy={busy}
              label={t('auth.verifyRegistration.resend')}
              busyLabel={t('auth.verifyRegistration.resending')}
            />
          </button>
        ) : (
          <Link to="/register" className="btn-primary mt-6 w-full">
            {t('auth.login.register')}
          </Link>
        )}

        <button
          type="button"
          className="btn-ghost mt-1 w-full"
          onClick={() =>
            navigate('/login?setupProfile=1', { replace: true })
          }
        >
          {t('auth.verifyRegistration.backToLogin')}
        </button>
      </div>
    </AuthLayout>
  )
}
